/* eslint-disable jsx-a11y/label-has-associated-control */
// This is safe to ignore because we're using implicit label controls

import React, { useCallback, useMemo, useState } from 'react'

import { navigate } from 'gatsby'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import HTTPRequestService from '../../services/HTTPRequestService'
import { above, colors, space } from '../../style/design-system'
import { formatPhoneNumber, maskPhone } from '../../utils/phoneNumberFormatter'
import { phoneNumbers } from '../../utils/static-data/phoneNumbers'
import {
  FormField,
  newFormField,
  Validator,
  required,
  min,
  email,
  phone,
} from '../../utils/validator'

type FormData = {
  Email: FormField
  FullName: FormField
  Organization: FormField
  Phone: FormField
  Purpose: FormField
  State: FormField
  PositionLevel: FormField
}

const FormEmployers = ({ className }) => {
  // create an instantiator for Validator, the signature of which won't change and
  // therefore will not cause a re-render
  const newValidator = useCallback(
    () =>
      new Validator<FormData>({
        Email: newFormField('', required(), email()),
        FullName: newFormField('', required(), min(2)),
        Organization: newFormField('', required(), min(2)),
        Phone: newFormField('', required(), phone()),
        PositionLevel: newFormField('', required()),
        Purpose: newFormField('', required()),
        State: newFormField('', required()),
      }),
    [],
  )

  // create a validator instance
  const v = useMemo(() => newValidator(), [newValidator])

  // local state
  const [userPhoneNumber, setUserPhoneNumber] = useState(
    v.fields.Phone.getValue(),
  )
  const [userState, setUserState] = useState(v.fields.State.getValue())
  const [userPurpose, setUserPurpose] = useState(v.fields.Purpose.getValue())
  const [positionLevel, setPositionLevel] = useState(
    v.fields.Purpose.getValue(),
  )
  const [formIsSubmitting, setFormIsSubmitting] = useState(false)
  const [formErrorMessage, setFormErrorMessage] = useState('')
  const [formFieldErrors, setFormFieldErrors] = useState(v.getErrors())

  // create a localized function to validate the fields
  function validateField(fieldID: string, options?: { blur: boolean }) {
    const field = v.validateField(fieldID, options)
    if (field.blurred) {
      setFormFieldErrors({
        ...formFieldErrors,
        [fieldID]: field.errors,
      })
    }
    const { valid } = v.validate()
    if (valid) {
      setFormErrorMessage('')
    }
  }

  // set field value and validate onChange and onInput (they are functionally combined
  // by React into onChange)
  const handleChange =
    (fieldID: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      let { value } = e.target
      if (fieldID === 'Phone') {
        value = maskPhone(value)
        setUserPhoneNumber(value)
      }
      if (fieldID === 'Purpose') {
        setUserPurpose(value)
      }
      if (fieldID === 'State') {
        setUserState(value)
      }
      if (fieldID === 'PositionLevel') {
        setPositionLevel(value)
      }
      v.fields[fieldID].setValue(value)
      validateField(fieldID)
    }

  // form submission handler
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    let emailSuccessful = false
    let salesforceSuccessful = false

    e.preventDefault()

    // blur all the form fields
    v.blurAll()

    // form is now submitting
    setFormIsSubmitting(true)

    // reset the form error message and validate
    setFormErrorMessage('')
    const { valid, errors } = v.validate()
    setFormFieldErrors(errors)

    // if not valid set the form error message, set form is not submitting, and return
    if (!valid) {
      setFormErrorMessage('Please ensure all required fields are accurate.')
      setFormIsSubmitting(false)
      return
    }

    // get the validated form data
    const data = v.getData()

    // set up a client
    const client = new HTTPRequestService()

    // send email notification
    try {
      const subject = 'New Submission from the Hire UMA Form'
      const body = `
        A new submission from the Hire UMA form has been received.

        Name: ${data.FullName}
        Organization: ${data.Organization}
        State: ${data.State}
        Email: ${data.Email}
        Phone Number: ${data.Phone}
        Position Level: ${data.PositionLevel}
        Purpose: ${data.Purpose}
      `
      // send email
      await client.post<{ body: string; subject: string }>(
        '/.netlify/functions/employerEmail',
        { body, subject },
      )

      emailSuccessful = true
    } catch (err) {
      emailSuccessful = false
    }

    // send to salesforce
    try {
      await client.post('/.netlify/functions/employerSalesforce', {
        dateadded: new Date(),
        email: data.Email,
        name: data.FullName,
        organization: data.Organization,
        phone: data.Phone,
        positionLevel: data.PositionLevel,
        purpose: data.Purpose,
        state: data.State,
      })
      salesforceSuccessful = true
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      salesforceSuccessful = false
    }

    if (salesforceSuccessful) {
      navigate('/confirmation/employers')
    } else {
      setFormErrorMessage(
        `We're sorry but something went wrong with your form submission. Please try again or call us at ${formatPhoneNumber(
          phoneNumbers.umaOnline,
        )}.`,
      )
      setFormIsSubmitting(false)
    }
  }

  return (
    <form className={className} onSubmit={handleSubmit}>
      <header className="text-center">
        {formErrorMessage && (
          <div className="text-center my-6">
            <p className="form-error--main p-3 px-5">{formErrorMessage}</p>
          </div>
        )}
      </header>

      <div className="d-block d-sm-flex inputs-wrap">
        <div className="input-wrap">
          <label>
            <span className="label-and-error d-flex">
              <span>Full Name</span>
              {!!formFieldErrors.FullName.length && (
                <span className="form-error--field">
                  {formFieldErrors.FullName[0]}
                </span>
              )}
            </span>
            <input
              name="FullName"
              type="text"
              onChange={handleChange('FullName')}
              onBlur={() => {
                validateField('FullName', { blur: true })
              }}
            />
          </label>
        </div>

        <div className="input-wrap">
          <label>
            <span className="label-and-error d-flex">
              <span>Organization</span>
              {!!formFieldErrors.Organization.length && (
                <span className="form-error--field">
                  {formFieldErrors.Organization[0]}
                </span>
              )}
            </span>
            <input
              name="Organization"
              type="text"
              onChange={handleChange('Organization')}
              onBlur={() => {
                validateField('Organization', { blur: true })
              }}
            />
          </label>
        </div>

        <div className="input-wrap">
          <label>
            <span className="label-and-error d-flex">
              <span>Phone</span>
              {!!formFieldErrors.Phone.length && (
                <span className="form-error--field">
                  {formFieldErrors.Phone[0]}
                </span>
              )}
            </span>
            <input
              name="Phone"
              type="tel"
              value={userPhoneNumber}
              onChange={handleChange('Phone')}
              onBlur={() => {
                validateField('Phone', { blur: true })
              }}
            />
          </label>
        </div>

        <div className="input-wrap">
          <label>
            <span className="label-and-error d-flex">
              <span>Email</span>
              {!!formFieldErrors.Email.length && (
                <span className="form-error--field">
                  {formFieldErrors.Email[0]}
                </span>
              )}
            </span>
            <input
              name="Email"
              type="email"
              onChange={handleChange('Email')}
              onBlur={() => {
                validateField('Email', { blur: true })
              }}
            />
          </label>
        </div>

        <div className="input-wrap">
          <label>
            <span className="label-and-error d-flex">
              <span>State</span>
              {!!formFieldErrors.State.length && (
                <span className="form-error--field">
                  {formFieldErrors.State[0]}
                </span>
              )}
            </span>
            <select
              name="State"
              value={userState}
              onChange={handleChange('State')}
              onBlur={() => {
                validateField('State', { blur: true })
              }}>
              <option value="">Select One</option>
              <option value="Alabama">Alabama</option>
              <option value="Alaska">Alaska</option>
              <option value="Arizona">Arizona</option>
              <option value="Arkansas">Arkansas</option>
              <option value="California">California</option>
              <option value="Colorado">Colorado</option>
              <option value="Connecticut">Connecticut</option>
              <option value="Delaware">Delaware</option>
              <option value="District Of Columbia">District Of Columbia</option>
              <option value="Florida">Florida</option>
              <option value="Georgia">Georgia</option>
              <option value="Hawaii">Hawaii</option>
              <option value="Idaho">Idaho</option>
              <option value="Illinois">Illinois</option>
              <option value="Indiana">Indiana</option>
              <option value="Iowa">Iowa</option>
              <option value="Kansas">Kansas</option>
              <option value="Kentucky">Kentucky</option>
              <option value="Louisiana">Louisiana</option>
              <option value="Maine">Maine</option>
              <option value="Maryland">Maryland</option>
              <option value="Massachusetts">Massachusetts</option>
              <option value="Michigan">Michigan</option>
              <option value="Minnesota">Minnesota</option>
              <option value="Mississippi">Mississippi</option>
              <option value="Missouri">Missouri</option>
              <option value="Montana">Montana</option>
              <option value="Nebraska">Nebraska</option>
              <option value="Nevada">Nevada</option>
              <option value="New Hampshire">New Hampshire</option>
              <option value="New Jersey">New Jersey</option>
              <option value="New Mexico">New Mexico</option>
              <option value="New York">New York</option>
              <option value="North Carolina">North Carolina</option>
              <option value="North Dakota">North Dakota</option>
              <option value="Ohio">Ohio</option>
              <option value="Oklahoma">Oklahoma</option>
              <option value="Oregon">Oregon</option>
              <option value="Pennsylvania">Pennsylvania</option>
              <option value="Rhode Island">Rhode Island</option>
              <option value="South Carolina">South Carolina</option>
              <option value="South Dakota">South Dakota</option>
              <option value="Tennessee">Tennessee</option>
              <option value="Texas">Texas</option>
              <option value="Utah">Utah</option>
              <option value="Vermont">Vermont</option>
              <option value="Virginia">Virginia</option>
              <option value="Washington">Washington</option>
              <option value="West Virginia">West Virginia</option>
              <option value="Wisconsin">Wisconsin</option>
              <option value="Wyoming">Wyoming</option>
            </select>
          </label>
        </div>

        <div className="input-wrap">
          <label>
            <span className="label-and-error d-flex">
              <span>Purpose</span>
              {!!formFieldErrors.Purpose.length && (
                <span className="form-error--field">
                  {formFieldErrors.Purpose[0]}
                </span>
              )}
            </span>
            <select
              name="Purpose"
              value={userPurpose}
              onChange={handleChange('Purpose')}
              onBlur={() => {
                validateField('Purpose', { blur: true })
              }}>
              <option value="">Select One</option>
              <option value="Need to fill a vacant position immediately">
                Need to fill a vacant position immediately
              </option>
              <option value="Need to fill a position in the next 30-90 days">
                Need to fill a position in the next 30-90 days
              </option>
              <option value="Looking to fill a position in the next 90 days or more">
                Looking to fill a position in the next 90 days or more
              </option>
              <option value="Looking for information to partner with UMA for future staffing needs">
                Looking for information to partner with UMA for future staffing
                needs
              </option>
              <option value="Looking for a job in the allied healthcare field">
                Looking for a job in the allied healthcare field
              </option>
            </select>
          </label>
        </div>

        <div className="input-wrap">
          <label>
            <span className="label-and-error d-flex">
              <span>Position Level</span>
              {!!formFieldErrors.PositionLevel.length && (
                <span className="form-error--field">
                  {formFieldErrors.PositionLevel[0]}
                </span>
              )}
            </span>
            <select
              name="PositionLevel"
              value={positionLevel}
              onChange={handleChange('PositionLevel')}
              onBlur={() => {
                validateField('PositionLevel', { blur: true })
              }}>
              <option value="">Select One</option>
              <option value="Entry level">Entry-level</option>
              <option value="Mid level">Mid-level</option>
              <option value="Senior level">Senior-level</option>
            </select>
          </label>
        </div>
      </div>

      <div className="submit-wrap text-center mt-6">
        <input type="submit" value="Continue" disabled={formIsSubmitting} />
      </div>
    </form>
  )
}

FormEmployers.propTypes = {
  className: PropTypes.string,
}

FormEmployers.defaultProps = {
  className: '',
}

// @ts-ignore
export default styled(FormEmployers)`
  header {
    width: 100%;
  }

  label {
    width: 100%;
  }

  input:not([type='submit']),
  select {
    background-color: #fff;
    cursor: pointer;
    max-width: 100%;
    width: 100%;
  }

  input[type='submit'] {
    background-color: hsl(86.9, 70.1%, 38%);
    background-image: linear-gradient(
      -180deg,
      hsl(87.1, 69.8%, 44.1%),
      hsl(86.9, 70.1%, 38%) 90%
    );
    border: none;
    color: #fff;
    margin: 0 auto;
    padding: ${space.c} ${space.e};
    &:hover,
    &:focus {
      background-color: hsl(86.9, 70.1%, 38%);
      background-image: none;
      color: hsl(0, 0%, 100%);
    }
    &[disabled] {
      background-color: ${colors.dev.gray.c()};
      background-image: none;
    }
  }

  .form-error--main {
    background-color: ${colors.dev.red.f()};
    color: #fff;
    display: inline-block;
    font-size: 1rem;
    margin: 0 auto;
  }

  .inputs-wrap {
    flex-wrap: wrap;
  }

  .submit-wrap {
    margin: ${space.e} auto;
  }

  .input-wrap {
    flex: 0 0 50%;
    ${above.sm`
      &:nth-of-type(odd) {
        padding-right: 0.5rem;
      }
      &:nth-of-type(even) {
        padding-left: 0.5rem;
      }
    `}
  }

  .label-and-error {
    align-items: baseline;
    justify-content: space-between;
    width: 100% !important;
  }

  .form-error--field,
  form-error--main {
    background-color: transparent;
    color: ${colors.dev.red.f()};
  }

  .form-error--field {
    font-size: 0.8rem;
  }
`
